import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Section from "@/pages/settings/components/sections/Section";
import AddActionButton from "@/components/ui/add-action-button";
import { useFieldManagement } from "./hooks";
import { AddFieldDrawer } from "./AddFieldDrawer";
import { EditCustomFieldDrawer } from "./EditCustomFieldDrawer";
import { EditEditableSystemFieldDrawer } from "./EditEditableSystemFieldDrawer";
import { FieldManagementTable } from "./FieldManagementTable";
import Spinner from "@/components/ui/spinner";
import {
  EntityType,
  entityTypeToSingleEntityName
} from "@/services/hooks/entities/useEntityType";
import { APIFieldItem } from "@/pages/settings/types";

const TRANSLATION_KEY_TABS = "settings.deals";

interface FieldManagementTabProps {
  entityType?: EntityType;
}

export const FieldManagementTab: React.FC<FieldManagementTabProps> = ({
  entityType = EntityType.DEAL
}) => {
  const { t } = useTranslation();
  const [isAddFieldDrawerOpen, setIsAddFieldDrawerOpen] = useState(false);
  const [isEditFieldDrawerOpen, setIsEditFieldDrawerOpen] = useState(false);
  const [
    isEditEditableSystemFieldDrawerOpen,
    setIsEditEditableSystemFieldDrawerOpen
  ] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState<APIFieldItem | null>(null);

  const entityTypeName = entityTypeToSingleEntityName[entityType];

  const {
    fields,
    headerFields,
    isLoading,
    handleDeleteField,
    handleToggleFieldVisibility,
    handleDragEnd,
    anchoredFieldKeys,
    hideFieldEnabled
  } = useFieldManagement(entityType);

  const noop = () => {};

  const handleAddFieldClick = () => {
    setIsAddFieldDrawerOpen(true);
  };

  const handleEditFieldClick = (field: APIFieldItem) => {
    setFieldToEdit(field);
    if (field.fieldManagement?.canEditDefinition) {
      setIsEditFieldDrawerOpen(true);
    } else if (field.fieldManagement) {
      setIsEditEditableSystemFieldDrawerOpen(true);
    }
  };

  const handleEditFieldSave = () => {
    setIsEditFieldDrawerOpen(false);
    setIsEditEditableSystemFieldDrawerOpen(false);
    setFieldToEdit(null);
  };

  const handleEditFieldDelete = (fieldId: string) => {
    handleDeleteField(fieldId);
    setIsEditFieldDrawerOpen(false);
    setFieldToEdit(null);
  };

  useEffect(() => {
    if (
      isAddFieldDrawerOpen ||
      isEditFieldDrawerOpen ||
      isEditEditableSystemFieldDrawerOpen
    ) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [
    isAddFieldDrawerOpen,
    isEditFieldDrawerOpen,
    isEditEditableSystemFieldDrawerOpen
  ]);

  return (
    <>
      {hideFieldEnabled && !isLoading && headerFields.length > 0 && (
        <Section
          title={t(`${TRANSLATION_KEY_TABS}.fieldManagement.header.title`)}
          subTitle={t(`${TRANSLATION_KEY_TABS}.fieldManagement.header.subTitle`, {
            entityType: entityTypeName
          })}
        >
          <FieldManagementTable
            entityType={entityType}
            fields={headerFields}
            onDragEnd={noop}
            onEditField={noop}
            onDeleteField={noop}
            hideFieldEnabled={hideFieldEnabled}
            disableDragAndDrop
          />
        </Section>
      )}

      <Section
        title={t(`${TRANSLATION_KEY_TABS}.fieldManagement.title`)}
        subTitle={t(`${TRANSLATION_KEY_TABS}.fieldManagement.subTitle`, {
          entityTypeCapitalized: t(`entities.names.${entityTypeName}`),
          entityType: entityTypeName
        })}
        action={
          <AddActionButton
            label={t(`${TRANSLATION_KEY_TABS}.fieldManagement.add`)}
            onClick={handleAddFieldClick}
            disabled={isLoading}
          />
        }
        subTitleBreakAfterSentence
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <FieldManagementTable
            entityType={entityType}
            fields={fields}
            onDragEnd={handleDragEnd}
            onEditField={handleEditFieldClick}
            onDeleteField={handleDeleteField}
            onToggleVisibility={handleToggleFieldVisibility}
            hideFieldEnabled={hideFieldEnabled}
            anchoredFieldKeys={anchoredFieldKeys}
            useEnhancedDeleteConfirmation
            deleteCheckboxLabel={t(
              `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.checkboxLabel`
            )}
          />
        )}
      </Section>

      <AddFieldDrawer
        isOpen={isAddFieldDrawerOpen}
        onOpenChange={setIsAddFieldDrawerOpen}
        entityType={entityType}
      />

      <EditCustomFieldDrawer
        isOpen={isEditFieldDrawerOpen}
        onOpenChange={setIsEditFieldDrawerOpen}
        fieldData={fieldToEdit}
        entityType={entityType}
        onSave={handleEditFieldSave}
        onDelete={handleEditFieldDelete}
      />

      <EditEditableSystemFieldDrawer
        key={fieldToEdit?.fieldKey}
        isOpen={isEditEditableSystemFieldDrawerOpen}
        onOpenChange={setIsEditEditableSystemFieldDrawerOpen}
        fieldData={fieldToEdit}
        entityType={entityType}
        onSave={handleEditFieldSave}
      />
    </>
  );
};
